import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { formatDateInIsrael } from '../utils/dateUtils';
import './WaitlistPage.css';

function WaitlistPage() {
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [displayTransactions, setDisplayTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 50
  });
  
  // Modal states
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  
  // Action modal state
  const [actionType, setActionType] = useState(null);
  const [matchedCustomers, setMatchedCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [matchSearchTerm, setMatchSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [processingAction, setProcessingAction] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    loadPendingTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  useEffect(() => {
    applyFiltersAndPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTransactions, searchTerm]);

  const loadPendingTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/webhook/pending-customers?status=${filterStatus}`);
      const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
      
      const validTransactions = data.filter(t => t && (t.id || t.zeout));
      setPendingTransactions(validTransactions);
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    } catch (error) {
      console.error('Error loading pending transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  const applyFiltersAndPagination = useCallback(() => {
    const filteredTransactions = pendingTransactions.filter(transaction => {
      const matchesSearch = !searchTerm.trim() || 
        (transaction.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (transaction.phone || '').includes(searchTerm) ||
        (transaction.zeout || '').includes(searchTerm) ||
        (transaction.groupe || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesSearch;
    });

    const totalItems = filteredTransactions.length;
    const limit = pagination.limit;
    const totalPages = Math.ceil(totalItems / limit) || 1;
    const currentPage = Math.min(pagination.currentPage, totalPages);
    
    const offset = (currentPage - 1) * limit;
    const paginatedTransactions = filteredTransactions.slice(offset, offset + limit);
    
    setDisplayTransactions(paginatedTransactions);
    setPagination(prev => ({
      ...prev,
      currentPage,
      totalPages,
      totalItems
    }));
  }, [pendingTransactions, searchTerm, pagination.currentPage, pagination.limit]);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleViewDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionModal(true);
  };

  const handleApproveClick = (transaction) => {
    setSelectedTransaction(transaction);
    setShowActionModal(true);
    setActionType(null);
    setMatchedCustomers([]);
    setSelectedCustomerId(null);
    setMatchSearchTerm('');
  };

  const handleRejectClick = (transaction) => {
    setSelectedTransaction(transaction);
    setShowRejectModal(true);
  };

  const searchMatchingCustomers = async () => {
    if (!matchSearchTerm.trim() && !selectedTransaction) return;
    
    try {
      setLoading(true);
      const searchParams = {
        zeout: selectedTransaction?.zeout,
        phone: matchSearchTerm.includes('05') ? matchSearchTerm : undefined,
        name: !matchSearchTerm.includes('05') ? matchSearchTerm : undefined
      };
      
      Object.keys(searchParams).forEach(key => 
        searchParams[key] === undefined && delete searchParams[key]
      );
      
      const response = await api.get('/webhook/match-customers', { params: searchParams });
      const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
      setMatchedCustomers(data);
    } catch (error) {
      console.error('Error searching customers:', error);
      setMatchedCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAction = (type) => {
    setActionType(type);
    
    if (type === 'create_new') {
      handleConfirmApprove('create_new', null);
    } else if (type === 'add_subscription' || type === 'extend_subscription') {
      searchMatchingCustomers();
    }
  };

  const handleConfirmApprove = async (action, customerId) => {
    try {
      setProcessingAction(true);
      const payload = {
        action: action,
        customerId: customerId || null,
        reason: ''
      };
      
      await api.post(`/webhook/approve/${selectedTransaction.id}`, payload);
      
      setShowActionModal(false);
      setActionType(null);
      setMatchedCustomers([]);
      setSelectedCustomerId(null);
      setMatchSearchTerm('');
      
      await loadPendingTransactions();
      showSuccess(`✅ הערך "${selectedTransaction.name}" אושר בהצלחה`);
    } catch (error) {
      console.error('Error approving transaction:', error);
      alert('שגיאה באישור הערך: ' + (error.response?.data?.error || error.message));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleConfirmReject = async () => {
    try {
      setLoading(true);
      await api.post(`/webhook/reject/${selectedTransaction.id}`, {
        reason: rejectReason
      });
      
      setShowRejectModal(false);
      setRejectReason('');
      await loadPendingTransactions();
      showSuccess(`❌ הערך "${selectedTransaction.name}" דחה בהצלחה`);
    } catch (error) {
      console.error('Error rejecting transaction:', error);
      alert('שגיאה בדחיית הערך: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (categorie) => {
    const categoryMap = {
      1: 'משתתפי מחוג',
      2: 'מנויים שנתי',
      3: 'מנויים קצר טווח'
    };
    return categoryMap[categorie] || `קטגוריה ${categorie}`;
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'pending': '⏳ בהמתנה',
      'approved': '✅ אושר',
      'rejected': '❌ נדחה'
    };
    return statusMap[status] || status;
  };

  return (
    <div className="waitlist-page">
      <div className="page-header">
        <h1>📋 רשימת ההמתנה</h1>
        <p>ניהול עסקאות מערכת התשלום שבהמתנה לאישור</p>
      </div>

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      <div className="filters-bar">
        <div className="filter-group">
          <label>סטטוס:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} disabled={loading}>
            <option value="pending">⏳ בהמתנה</option>
            <option value="approved">✅ אושר</option>
            <option value="rejected">❌ נדחה</option>
            <option value="">הכל</option>
          </select>
        </div>

        <div className="filter-group">
          <input
            type="text"
            placeholder="חיפוש לפי שם, טלפון, תעודה..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            disabled={loading}
          />
        </div>

        <div className="filter-info">
          סה״כ: {pagination.totalItems} | עמוד {pagination.currentPage} מתוך {pagination.totalPages}
        </div>
      </div>

      <div className="table-container">
        {loading ? (
          <div className="loading">⏳ טוען...</div>
        ) : displayTransactions.length === 0 ? (
          <div className="no-data"><p>אין עסקאות בחר בסטטוס זה</p></div>
        ) : (
          <table className="transactions-table">
            <thead>
              <tr>
                <th>שם</th>
                <th>טלפון</th>
                <th>סכום</th>
                <th>ספרים</th>
                <th>קטגוריה</th>
                <th>תאריך</th>
                <th>סטטוס</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {displayTransactions.map((transaction) => (
                <tr key={transaction.id || transaction.zeout}>
                  <td className="name-cell">
                    <strong>{transaction.name}</strong>
                    {transaction.zeout && <div className="zeout">ת.ז: {transaction.zeout}</div>}
                  </td>
                  <td>{transaction.phone || '—'}</td>
                  <td className="amount-cell"><strong>{transaction.amount || '—'}₪</strong></td>
                  <td className="books-cell"><span className="badge">{transaction.books_in_subscription || 0}</span></td>
                  <td>{getCategoryLabel(transaction.category_id || transaction.groupe)}</td>
                  <td className="date-cell">
                    {transaction.created_at ? formatDateInIsrael(new Date(transaction.created_at)) : '—'}
                  </td>
                  <td>
                    <span className={`status-badge status-${transaction.status || 'pending'}`}>
                      {getStatusLabel(transaction.status || 'pending')}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button className="btn-secondary btn-sm" onClick={() => handleViewDetails(transaction)} disabled={loading} title="צפה בפרטים">📋</button>
                    {(!transaction.status || transaction.status === 'pending') && (
                      <>
                        <button className="btn-success btn-sm" onClick={() => handleApproveClick(transaction)} disabled={loading} title="אשר">✅</button>
                        <button className="btn-danger btn-sm" onClick={() => handleRejectClick(transaction)} disabled={loading} title="דחה">❌</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))} disabled={pagination.currentPage === 1 || loading}>← קודם</button>
          <span className="page-info">{pagination.currentPage} / {pagination.totalPages}</span>
          <button onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))} disabled={pagination.currentPage === pagination.totalPages || loading}>הבא →</button>
        </div>
      )}

      {/* Transaction Details Modal */}
      {showTransactionModal && selectedTransaction && (
        <div className="modal-overlay" onClick={() => setShowTransactionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>פרטי העסקה</h2>
              <button className="close-btn" onClick={() => setShowTransactionModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="transaction-details">
                <div className="detail-row"><span className="label">שם:</span><span className="value">{selectedTransaction.name}</span></div>
                <div className="detail-row"><span className="label">תעודת זהות:</span><span className="value">{selectedTransaction.zeout || '—'}</span></div>
                <div className="detail-row"><span className="label">טלפון:</span><span className="value">{selectedTransaction.phone || '—'}</span></div>
                <div className="detail-row"><span className="label">דוא״ל:</span><span className="value">{selectedTransaction.email || '—'}</span></div>
                <div className="detail-row"><span className="label">סכום:</span><span className="value">{selectedTransaction.amount || '—'}₪</span></div>
                <div className="detail-row"><span className="label">ספרים:</span><span className="value">{selectedTransaction.books_in_subscription || 0}</span></div>
                <div className="detail-row"><span className="label">קטגוריה:</span><span className="value">{getCategoryLabel(selectedTransaction.category_id || selectedTransaction.groupe)}</span></div>
                <div className="detail-row"><span className="label">קבוצה:</span><span className="value">{selectedTransaction.groupe || '—'}</span></div>
                <div className="detail-row"><span className="label">תאריך:</span><span className="value">{selectedTransaction.created_at ? formatDateInIsrael(new Date(selectedTransaction.created_at)) : '—'}</span></div>
                <div className="detail-row"><span className="label">ID עסקה:</span><span className="value">{selectedTransaction.transaction_id || '—'}</span></div>
                <div className="detail-row"><span className="label">סטטוס:</span><span className={`status-badge status-${selectedTransaction.status || 'pending'}`}>{getStatusLabel(selectedTransaction.status || 'pending')}</span></div>
              </div>
            </div>
            <div className="modal-footer"><button className="btn-secondary" onClick={() => setShowTransactionModal(false)}>סגור</button></div>
          </div>
        </div>
      )}

      {/* Action Selection Modal */}
      {showActionModal && selectedTransaction && (
        <div className="modal-overlay" onClick={() => setShowActionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>בחר פעולה</h2>
              <button className="close-btn" onClick={() => setShowActionModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {!actionType ? (
                <div className="action-choices">
                  <div className="action-buttons">
                    <button className="action-btn action-create" onClick={() => handleSelectAction('create_new')} disabled={processingAction}>
                      <span className="action-icon">➕</span>
                      <span className="action-text">יצור לקוח חדש</span>
                    </button>
                    <button className="action-btn action-add" onClick={() => handleSelectAction('add_subscription')} disabled={processingAction}>
                      <span className="action-icon">📚</span>
                      <span className="action-text">הוסף מנוי ללקוח קיים</span>
                    </button>
                    <button className="action-btn action-extend" onClick={() => handleSelectAction('extend_subscription')} disabled={processingAction}>
                      <span className="action-icon">🔄</span>
                      <span className="action-text">הרחב מנוי קיים</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="customer-search">
                  <h3>{actionType === 'add_subscription' ? 'הוסף מנוי' : 'הרחב מנוי'}</h3>
                  <div className="search-group">
                    <input type="text" placeholder="חיפוש לפי שם או טלפון..." value={matchSearchTerm} onChange={(e) => setMatchSearchTerm(e.target.value)} className="search-input" disabled={processingAction || loading} />
                    <button className="btn-secondary" onClick={searchMatchingCustomers} disabled={processingAction || loading}>🔍 חפש</button>
                  </div>
                  {matchedCustomers.length > 0 ? (
                    <div className="matched-customers">
                      <p>תוצאות חיפוש ({matchedCustomers.length}):</p>
                      <div className="customers-list">
                        {matchedCustomers.map((customer) => (
                          <div key={customer.identity_id} className={`customer-item ${selectedCustomerId === customer.identity_id ? 'selected' : ''}`} onClick={() => setSelectedCustomerId(customer.identity_id)}>
                            <div className="customer-name">{customer.name}</div>
                            <div className="customer-details">
                              <span>ת.ז: {customer.identity_id}</span>
                              <span>טלפון: {customer.phone || '—'}</span>
                              <span>ספרים: {customer.books_in_subscription}</span>
                              <span>תאריך סיום: {formatDateInIsrael(new Date(customer.subscription_end_date))}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : matchSearchTerm && <div className="no-results">לא נמצאו לקוחות תואמים</div>}
                </div>
              )}
            </div>
            <div className="modal-footer">
              {actionType && (actionType === 'add_subscription' || actionType === 'extend_subscription') ? (
                <>
                  <button className="btn-secondary" onClick={() => setActionType(null)} disabled={processingAction}>חזור</button>
                  <button className="btn-success" onClick={() => handleConfirmApprove(actionType, selectedCustomerId)} disabled={processingAction || !selectedCustomerId}>{processingAction ? '⏳ עיבוד...' : '✅ אשר'}</button>
                </>
              ) : (
                <button className="btn-secondary" onClick={() => setShowActionModal(false)} disabled={processingAction}>סגור</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedTransaction && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>❌ דחה עסקה</h2>
              <button className="close-btn" onClick={() => setShowRejectModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>האם אתה בטוח שברצונך לדחות את העסקה של <strong>{selectedTransaction.name}</strong>?</p>
              <textarea placeholder="סיבת הדחייה (מומלץ)..." value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows="3" className="textarea" required />
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowRejectModal(false)} disabled={loading}>ביטול</button>
              <button className="btn-danger" onClick={handleConfirmReject} disabled={loading || !rejectReason.trim()}>{loading ? '⏳ מעדכן...' : '❌ דחה'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WaitlistPage;
